// Guide sheet footer (NAV-G9). Claude Design (20) "Guide - *.dc.html" puts a
// footer slot in each doc-page: "Chamber Member Pro™ Module Guide", the page
// name and the copyright line, once at the bottom of the sheet on screen and
// on every printed page (doc-page.js running footer). The published guide
// HTML has no footer yet (the publish script strips it), so the frame builds
// it from the page title. A published footer marked data-guide-footer (the
// design page name, e.g. "Invoices › Create Ticket Invoice") is used as is.

export const GUIDE_FOOTER_STRINGS = {
  module: "Chamber Member Pro™ Module Guide",
  copyright: "© 2026 Chamber Member Pro™, part of One Chamber System™. All rights reserved.",
} as const;

// Guide - Contents.dc.html:40-46 footer, and doc-page.js print rules: the
// footer repeats on each printed page (position: fixed) and a table footer
// spacer keeps the text above it (browsers repeat <tfoot> on each page).
export const GUIDE_FOOTER_STYLES = `
[data-guide-footer]{display:flex;flex-direction:column;gap:4px;margin-top:32px;font-family:'DM Sans',sans-serif;font-size:9pt;color:#727272;border-top:1px solid #ECECEC;padding-top:8px;}
[data-guide-footer-row]{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;}
[data-guide-footer-copyright]{text-align:center;}
[data-guide-print-table],[data-guide-print-table]>tbody,[data-guide-print-table]>tbody>tr,[data-guide-print-table]>tbody>tr>td{display:block;padding:0;}
[data-guide-print-table]>tfoot{display:none;}
@media print{
[data-guide-print-table]{display:table;width:100%;border-collapse:collapse;}
[data-guide-print-table]>tbody{display:table-row-group;}
[data-guide-print-table]>tbody>tr{display:table-row;}
[data-guide-print-table]>tbody>tr>td{display:table-cell;}
[data-guide-print-table]>tfoot{display:table-footer-group;}
[data-guide-print-spacer]{height:0.6in;}
[data-guide-footer]{position:fixed;left:0;right:0;bottom:0;margin:0;background:#fff;}
}
`;

const buildFooter = (doc: Document, pageName: string): HTMLElement => {
  const span = (text: string, attribute?: string) => {
    const element = doc.createElement("span");
    element.textContent = text;
    if (attribute) element.setAttribute(attribute, "true");
    return element;
  };
  const footer = doc.createElement("div");
  footer.setAttribute("data-guide-footer", "true");
  const row = doc.createElement("div");
  row.setAttribute("data-guide-footer-row", "true");
  row.append(span(GUIDE_FOOTER_STRINGS.module), span(pageName));
  footer.append(row, span(GUIDE_FOOTER_STRINGS.copyright, "data-guide-footer-copyright"));
  return footer;
};

/**
 * Adds the footer and the print spacer to the guide sheet once. Text goes in
 * with textContent, so the page title can never add markup. Returns false
 * when the frame has no guide sheet or is already done.
 */
export const addGuideFrameFooter = (doc: Document, pageName: string): boolean => {
  const page = doc.querySelector<HTMLElement>("[data-guide-page]");
  if (!page || page.querySelector("[data-guide-print-table]")) return false;
  const published = page.querySelector<HTMLElement>("[data-guide-footer]");
  published?.remove();
  const footer = published || buildFooter(doc, pageName);

  // Screen: plain blocks. Print: a one-cell table whose footer row is a
  // spacer of the footer height on every page.
  const table = doc.createElement("table");
  table.setAttribute("data-guide-print-table", "true");
  table.setAttribute("role", "presentation");
  const body = doc.createElement("tbody");
  const bodyRow = doc.createElement("tr");
  const cell = doc.createElement("td");
  cell.append(...Array.from(page.childNodes));
  bodyRow.append(cell);
  body.append(bodyRow);
  const foot = doc.createElement("tfoot");
  foot.setAttribute("aria-hidden", "true");
  const footRow = doc.createElement("tr");
  const footCell = doc.createElement("td");
  const spacer = doc.createElement("div");
  spacer.setAttribute("data-guide-print-spacer", "true");
  footCell.append(spacer);
  footRow.append(footCell);
  foot.append(footRow);
  table.append(body, foot);
  page.append(table, footer);
  return true;
};
